/**
 * InboxSplit — the shared two-lane Inbox body (Inbox V2, spec §4.1).
 *
 * Owns the data: the Needs-You query (open items), the Notifications cursor-
 * paginated pages, the badge count, and a 30s poll fallback. Both the full-page
 * route (inbox.lazy.tsx) and the bell popover/sheet (mentions-inbox.tsx) render
 * THIS so the two surfaces stay identical.
 *
 * The live inbox-events WebSocket is NOT opened here: it lives once at the app
 * shell (the always-mounted bell in mentions-inbox.tsx) so there is exactly ONE
 * subscription per tab and the founder is pinged even with the Inbox closed. The
 * shell handler invalidates inboxKeys.all on each frame, which re-fetches the
 * queries below, so this panel still updates live whether open or not.
 *
 * CROSS-ORG (founder 2026-07-05): the Inbox is the user's PERSONAL, GLOBAL view.
 * It uses the GLOBAL endpoints (GET /api/inbox…, /api/inbox/recent-cards) that
 * aggregate across ALL the user's orgs, NOT the single activeOrgId. Boards /
 * kanban stay per-org. Each row shows which org it belongs to (org badge).
 *
 * Layout: ONE vertical scroll on BOTH surfaces — Needs You sticky at top (badge
 * counts ONLY open needs-you items), Notifications below (its own capped inner
 * scroller), then Recent tasks. Optimistic dismiss removes a notification row
 * immediately; the WS/poll reconciles.
 *
 * ADR-057 monochrome, English only.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { env } from '../../env';
import { inboxApi, inboxKeys } from '../../lib/api/inbox';
import type { Mention } from '../../lib/api/mentions';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useToaster } from '../ui/toast';
import { NeedsYouList } from './needs-you-list';
import { NotificationsList } from './notifications-list';
import { RecentTasksList } from './recent-tasks-list';

const POLL_MS = 30_000;
const NOTIF_PAGE = 40;

interface InboxSplitProps {
  /** Called after a row click navigates (close the popover/sheet). */
  onNavigate?: () => void;
  /** Compact (popover/sheet) vs full-page heights. */
  variant?: 'page' | 'panel';
  className?: string;
}

export function InboxSplit({ onNavigate, variant = 'page', className }: InboxSplitProps) {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const { toast } = useToaster();
  // The Inbox is GLOBAL now — it only needs an authenticated user, NOT an
  // active org (it spans all the user's orgs).
  const enabled = !!me;

  // NOTE: no useInboxEvents() here. The single inbox WS lives at the app shell
  // (mentions-inbox.tsx); its per-frame invalidation of inboxKeys.all re-fetches
  // the queries below, so this panel live-updates without a second socket.

  // ── Needs You (open items, no cursor) ──────────────────────────────
  const needsYouQ = useQuery({
    queryKey: inboxKeys.needsYouGlobal(),
    queryFn: () => inboxApi.listNeedsYouGlobal(),
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
  const needsYou = needsYouQ.data?.items ?? [];

  // ── Notifications (cursor-paginated; pages accumulate locally) ──────
  // We page manually (the gateway returns { items, nextCursor }). The first page
  // is a react-query so the WS/poll invalidation re-fetches it; subsequent pages
  // are appended to local state and reset whenever the first page changes.
  const firstPageQ = useQuery({
    queryKey: inboxKeys.notificationsGlobal(),
    queryFn: () => inboxApi.listNotificationsGlobal(null, NOTIF_PAGE),
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  const [extraPages, setExtraPages] = React.useState<Mention[]>([]);
  const [cursor, setCursor] = React.useState<string | null>(null);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  // Locally-dismissed ids (optimistic) — hidden until the next refetch drops them.
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set());

  // Reset pagination + optimistic state whenever the first page re-lands (org
  // switch, WS invalidation) so we never show stale appended pages.
  const firstPageItems = firstPageQ.data?.items ?? [];
  const firstPageCursor = firstPageQ.data?.nextCursor ?? null;
  const firstPageStamp = firstPageItems[0]?.id ?? '∅';
  React.useEffect(() => {
    setExtraPages([]);
    setCursor(firstPageCursor);
    setDismissed(new Set());
  }, [firstPageStamp, firstPageCursor]);

  const loadMore = React.useCallback(async () => {
    if (!enabled || !cursor || isFetchingMore) return;
    setIsFetchingMore(true);
    try {
      const page = await inboxApi.listNotificationsGlobal(cursor, NOTIF_PAGE);
      setExtraPages((prev) => [...prev, ...page.items]);
      setCursor(page.nextCursor);
    } catch {
      /* leave cursor; next scroll retries */
    } finally {
      setIsFetchingMore(false);
    }
  }, [enabled, cursor, isFetchingMore]);

  const notifications = React.useMemo(() => {
    const all = [...firstPageItems, ...extraPages];
    // De-dup by id (a WS-invalidated first page can overlap an appended page).
    const seen = new Set<string>();
    const out: Mention[] = [];
    for (const m of all) {
      if (seen.has(m.id) || dismissed.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
    return out;
  }, [firstPageItems, extraPages, dismissed]);

  const dismissM = useMutation({
    mutationFn: (id: string) => inboxApi.dismiss(id),
    onMutate: (id: string) => {
      setDismissed((prev) => new Set(prev).add(id));
    },
    onError: (_e, id) => {
      // Roll back the optimistic hide.
      setDismissed((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast({ title: 'Could not dismiss', variant: 'destructive' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: inboxKeys.all });
    },
  });

  const needsYouCount = needsYou.length;
  const isPage = variant === 'page';

  // The scrolling root element. Captured as state (callback ref) so RecentTasksList
  // re-renders once it exists and re-attaches its IntersectionObserver to it —
  // inside the bounded popover/sheet the sentinel must observe THIS scroller, not
  // the viewport, or the next page never loads. On the page we pass null (viewport
  // root) to keep its proven behaviour unchanged.
  const [scrollEl, setScrollEl] = React.useState<HTMLDivElement | null>(null);

  return (
    // BOTH surfaces are ONE vertical scroll (this root scrolls) so all three
    // sections — Needs You, Notifications, Recent tasks — stack and are reached by
    // scrolling down. Needs You and Notifications each keep a bounded inner
    // scroller (caps below) so neither pushes Recent tasks out of reach, and the
    // Notifications virtualizer needs that bounded parent to measure. The popover/
    // sheet is height-bounded by its container, so `scrollEl` (this root) is the
    // scroll container the Recent-tasks sentinel observes (passed below).
    <div
      ref={setScrollEl}
      className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', className)}
    >
      {/* Needs You — sticky top with a badge counting only open needs-you.
          I1 (light): the section divider is dropped (space separates the lanes);
          the header is sentence-case, weight 500. I7/D5 (light): the panel cap is
          removed too — the whole Inbox is ONE scroll, so no nested scroller. */}
      <section
        className={cn('shrink-0', !env.VITE_DESIGN_LIGHT && 'border-b border-border')}
      >
        <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/95 px-4 py-2 backdrop-blur md:px-6">
          <h2
            className={
              env.VITE_DESIGN_LIGHT
                ? 'text-[12px] font-medium text-muted-foreground'
                : 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
            }
          >
            Needs you
          </h2>
          {needsYouCount > 0 && (
            <span
              className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-semibold leading-tight text-background"
              data-testid="needs-you-badge"
            >
              {needsYouCount}
            </span>
          )}
        </header>
        <div className={cn(!env.VITE_DESIGN_LIGHT && variant === 'panel' && 'max-h-[40vh] overflow-y-auto')}>
          <NeedsYouList items={needsYou} isLoading={needsYouQ.isLoading} onNavigate={onNavigate} />
        </div>
      </section>

      {/* Notifications — date-grouped, virtualized, infinite-scroll. Height-capped
          on BOTH surfaces (its own inner scroller — the virtualizer needs a bounded
          parent) so Recent tasks can follow below in the outer scroll: 60vh on the
          page, a touch tighter (45vh) in the bounded popover/sheet. */}
      <section
        className={cn(
          'flex min-h-0 shrink-0 flex-col',
          // I7/D5 (light): drop the vh caps — the list virtualizes against the
          // shared outer scroller (scrollParent below), so the Inbox is ONE
          // scroll with no nested scrollbars that collapse in landscape.
          !env.VITE_DESIGN_LIGHT && (isPage ? 'max-h-[60vh]' : 'max-h-[45vh]'),
        )}
      >
        <header className="flex items-center gap-2 px-4 py-2 md:px-6">
          <h2
            className={
              env.VITE_DESIGN_LIGHT
                ? 'text-[12px] font-medium text-muted-foreground'
                : 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
            }
          >
            Notifications
          </h2>
        </header>
        <NotificationsList
          items={notifications}
          isLoading={firstPageQ.isLoading}
          hasMore={cursor !== null}
          isFetchingMore={isFetchingMore}
          onLoadMore={() => void loadMore()}
          onDismiss={(id) => dismissM.mutate(id)}
          onNavigate={onNavigate}
          scrollParent={env.VITE_DESIGN_LIGHT ? scrollEl : undefined}
        />
      </section>

      {/* Recent tasks — now on BOTH surfaces (was page-only). Newest-first,
          keyset-paginated, lazily loaded on scroll. On the page the sentinel uses
          the viewport root (scrollRoot=null); in the bounded popover/sheet it must
          observe the panel's own scroll container (scrollEl) or it never
          intersects and the next page never loads. */}
      <RecentTasksList onNavigate={onNavigate} scrollRoot={isPage ? null : scrollEl} />
    </div>
  );
}
